import { onBeforeUnmount, onMounted, ref } from 'vue'
import { SPA_BUILD_ID } from '@/config/appBuild'
import { fetchSpaBuildMeta } from '@/services/spaBuildMeta'
import { isSpaStaleUiPreviewDev } from '@/services/spaStalePreview'

const POLL_MS = 3 * 60 * 1000
const SNOOZE_MS = 45 * 60 * 1000
const SNOOZE_KEY = 'crm_spa_stale_snooze_until'
/** Не дёргать мета-эндпоинт слишком часто при серии focus/visibility. */
const FOCUS_DEBOUNCE_MS = 4_000

function snoozeActive(): boolean {
  try {
    const raw = sessionStorage.getItem(SNOOZE_KEY)
    if (raw == null) {
      return false
    }
    const t = Number(raw)
    return Number.isFinite(t) && Date.now() < t
  } catch {
    return false
  }
}

export function useSpaStaleBuildWatcher() {
  const stale = ref(false)
  let timer: number | null = null
  let checkPromise: Promise<void> | null = null
  let focusDebounceTimer: number | null = null

  async function runCheck(): Promise<void> {
    if (checkPromise != null) {
      return checkPromise
    }
    checkPromise = doRunCheck().finally(() => {
      checkPromise = null
    })
    return checkPromise
  }

  async function doRunCheck(): Promise<void> {
    if (snoozeActive()) {
      stale.value = false
      return
    }
    if (isSpaStaleUiPreviewDev()) {
      stale.value = true
      return
    }
    if (SPA_BUILD_ID === 'local') {
      return
    }
    const meta = await fetchSpaBuildMeta()
    if (meta == null) {
      return
    }
    // После await пользователь мог нажать «Позже» — не поднимаем баннер снова
    if (snoozeActive()) {
      stale.value = false
      return
    }
    if (meta.build_id === 'local') {
      return
    }
    const serverId = meta.build_id.trim()
    const clientId = String(SPA_BUILD_ID).trim()
    if (serverId === clientId) {
      stale.value = false
      return
    }
    if (snoozeActive()) {
      stale.value = false
      return
    }
    stale.value = true
  }

  function snooze(): void {
    stale.value = false
    try {
      sessionStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
    } catch {
      /* ignore */
    }
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible') {
      void runCheck()
    }
  }

  function scheduleFocusCheck(): void {
    if (focusDebounceTimer != null) {
      window.clearTimeout(focusDebounceTimer)
    }
    focusDebounceTimer = window.setTimeout(() => {
      focusDebounceTimer = null
      void runCheck()
    }, FOCUS_DEBOUNCE_MS)
  }

  function onWindowFocus(): void {
    if (document.visibilityState === 'visible') {
      scheduleFocusCheck()
    }
  }

  function onOnline(): void {
    void runCheck()
  }

  function onPageShow(ev: Event): void {
    const pe = ev as PageTransitionEvent
    if (pe.persisted) {
      void runCheck()
    }
  }

  function onPwaSwUpdated(): void {
    // Источник правды — сравнение с GET /api/meta/spa-build, не сам controllerchange.
    void runCheck()
  }

  onMounted(() => {
    window.addEventListener('crm-pwa-sw-updated', onPwaSwUpdated)
    window.addEventListener('focus', onWindowFocus)
    window.addEventListener('online', onOnline)
    window.addEventListener('pageshow', onPageShow)
    if (isSpaStaleUiPreviewDev()) {
      stale.value = true
      document.addEventListener('visibilitychange', onVisibility)
      return
    }
    void runCheck()
    timer = window.setInterval(() => void runCheck(), POLL_MS)
    document.addEventListener('visibilitychange', onVisibility)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('crm-pwa-sw-updated', onPwaSwUpdated)
    window.removeEventListener('focus', onWindowFocus)
    window.removeEventListener('online', onOnline)
    window.removeEventListener('pageshow', onPageShow)
    if (timer != null) {
      window.clearInterval(timer)
      timer = null
    }
    if (focusDebounceTimer != null) {
      window.clearTimeout(focusDebounceTimer)
      focusDebounceTimer = null
    }
    document.removeEventListener('visibilitychange', onVisibility)
  })

  return {
    stale,
    snooze,
    checkNow: runCheck,
  }
}
